import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { api } from 'common';
import MaterialTable from 'material-table';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Badge,
  Button,
  Alert,
} from '@mui/material';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { ThemeProvider } from '@mui/material/styles';
import localization from '../components/Table/Localization';
import CircularLoading from '../components/CircularLoading';
import { useTranslation } from 'react-i18next';
import moment from 'moment/min/moment-with-locales';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import LeaderChangePanel from '../components/LeaderChangePanel';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  c2PillTabsSx,
  C2TableCard,
  C2SectionHeading,
  C2EmptyState,
  C2Select,
  C2SummaryCard,
  C2ProgressBar,
  C2StatusChip,
} from '../components/Table/C2UserTable';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const EMPTY = [];

function InfoRow({ label, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        py: 0.9,
        borderTop: '1px solid rgba(251,250,254,0.12)',
      }}
    >
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: 'rgba(251,250,254,0.7)' }}>{label}</Typography>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', fontWeight: 700, color: C2.white, textAlign: 'right' }}>
        {children}
      </Typography>
    </Box>
  );
}

function WeeklyChip({ ok, label }) {
  return <C2StatusChip tone={ok ? 'success' : 'danger'} label={label} />;
}

/**
 * Panel admin: Red de Líderes
 * Consolida ciudades, código master (abierto), cupos, primeros 100,
 * red N1–N3, cumplimiento semanal, historial y alertas.
 */
export default function RedLideres() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const {
    fetchUsersOnce,
    fetchZonificacionCities,
    fetchLeaderAlerts,
    fetchLeaderWeeklyHistory,
    editLeaderAlert,
    getMasterReferrerUidFromSettings,
    normalizeNetworkState,
    networkLevelLabel,
    formatDirectSlots,
    formatWeeklyProgress,
    resolveCitySlotUsage,
    reconcileCityLeaderSlots,
    DIRECT_SLOTS_PER_LEVEL,
    NETWORK_DISTRIBUTION_LABEL,
    WEEKLY_MIN_DRIVERS,
    WEEKLY_MIN_CUSTOMERS,
  } = api;

  const settings = useSelector((s) => s.settingsdata?.settings);
  const staticusers = useSelector((s) => s.usersdata?.staticusers);
  const cities = useSelector((s) => s.zonificacion?.cities) || EMPTY;
  const alerts = useSelector((s) => s.leaderAlerts?.alerts) || EMPTY;
  const history = useSelector((s) => s.leaderAlerts?.history) || EMPTY;
  const loadingUsers = useSelector((s) => s.usersdata?.loading);
  const loadingZonif = useSelector((s) => s.zonificacion?.loading);

  const [tab, setTab] = useState(0);
  const [cityFilter, setCityFilter] = useState('');
  const [alertStatusFilter, setAlertStatusFilter] = useState('');
  const [copied, setCopied] = useState(false);
  const [syncingSlots, setSyncingSlots] = useState(false);
  const [syncNotice, setSyncNotice] = useState(null);

  useEffect(() => {
    dispatch(fetchUsersOnce({ includeLocations: false }));
    dispatch(fetchZonificacionCities());
    if (fetchLeaderAlerts) dispatch(fetchLeaderAlerts());
    if (fetchLeaderWeeklyHistory) dispatch(fetchLeaderWeeklyHistory());
  }, [
    dispatch,
    fetchUsersOnce,
    fetchZonificacionCities,
    fetchLeaderAlerts,
    fetchLeaderWeeklyHistory,
  ]);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(id);
  }, [copied]);

  const masterUid =
    (getMasterReferrerUidFromSettings && getMasterReferrerUidFromSettings()) ||
    settings?.masterReferrerUid ||
    null;

  const masterUser = useMemo(() => {
    if (!masterUid || !staticusers) return null;
    return staticusers.find((u) => u.id === masterUid) || null;
  }, [masterUid, staticusers]);

  const drivers = useMemo(
    () => (staticusers || []).filter((u) => u.usertype === 'driver'),
    [staticusers]
  );

  const leaders = useMemo(
    () =>
      drivers.filter(
        (u) =>
          u.signupMode === 'city_special' ||
          (normalizeNetworkState &&
            normalizeNetworkState(u, u.id)?.inNetwork &&
            normalizeNetworkState(u, u.id)?.level === 0)
      ),
    [drivers, normalizeNetworkState]
  );

  const usersById = useMemo(() => {
    const m = {};
    (staticusers || []).forEach((u) => {
      m[u.id] = u;
    });
    return m;
  }, [staticusers]);

  const cityStats = useMemo(() => {
    return (cities || [])
      .filter((c) => c.active !== false)
      .map((c) => {
        const cityDrivers = drivers.filter((d) => d.cityId === c.id);
        const cityLeaders = leaders.filter((d) => d.cityId === c.id);
        // Barra = líderes reales (1ºs 100). No usar c.slotsUsed si quedó desfasado.
        const usage = resolveCitySlotUsage
          ? resolveCitySlotUsage({
              slotsUsedStored: c.slotsUsed,
              slotsTotal: c.slotsTotal,
              leadersCount: cityLeaders.length,
            })
          : {
              slotsUsed: cityLeaders.length,
              slotsTotal: Number(c.slotsTotal) || 100,
              slotsAvailable: Math.max(0, (Number(c.slotsTotal) || 100) - cityLeaders.length),
              drifted: false,
            };
        return {
          id: c.id,
          name: c.name,
          department: c.department || '',
          masterCode: c.masterCode || '',
          driversCount: cityDrivers.length,
          leadersCount: cityLeaders.length,
          slotsUsed: usage.slotsUsed,
          slotsTotal: usage.slotsTotal,
          slotsAvailable: usage.slotsAvailable,
          slotsDrifted: !!usage.drifted,
          active: c.active !== false,
        };
      })
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));
  }, [cities, drivers, leaders, resolveCitySlotUsage]);

  const driftedCityCount = useMemo(
    () => (cityStats || []).filter((c) => c.slotsDrifted).length,
    [cityStats]
  );

  const handleSyncCitySlots = async () => {
    if (!reconcileCityLeaderSlots) return;
    if (!settings?.AllowCriticalEditsAdmin) {
      setSyncNotice({
        severity: 'info',
        message: 'Modo demostración: no se puede escribir en la base. La barra ya muestra líderes reales en pantalla.',
      });
      return;
    }
    const rows = cityStats || [];
    if (!rows.length) {
      setSyncNotice({ severity: 'warning', message: 'No hay ciudades activas para sincronizar.' });
      return;
    }
    setSyncingSlots(true);
    setSyncNotice(null);
    let changed = 0;
    let errors = 0;
    try {
      for (const row of rows) {
        try {
          const res = await dispatch(reconcileCityLeaderSlots(row.id, row.leadersCount));
          if (res && res.changed) changed += 1;
        } catch (e) {
          errors += 1;
          console.warn('[RedLideres] sync', row.id, e);
        }
      }
      if (errors) {
        setSyncNotice({
          severity: 'warning',
          message: `Sincronización parcial: ${changed} ciudad(es) actualizada(s), ${errors} error(es).`,
        });
      } else if (changed === 0) {
        setSyncNotice({
          severity: 'success',
          message: 'Cupos al día: ninguna ciudad tenía desfase entre líderes y slotsUsed.',
        });
      } else {
        setSyncNotice({
          severity: 'success',
          message: `Cupos sincronizados: ${changed} ciudad(es) actualizada(s) con el conteo real de líderes.`,
        });
      }
    } finally {
      setSyncingSlots(false);
    }
  };

  const filteredLeaders = useMemo(() => {
    if (!cityFilter) return leaders;
    return leaders.filter((l) => l.cityId === cityFilter);
  }, [leaders, cityFilter]);

  const networkRows = useMemo(() => {
    const rows = [];
    drivers.forEach((d) => {
      const net = normalizeNetworkState
        ? normalizeNetworkState(d, d.id)
        : { inNetwork: false };
      if (!net.inNetwork && d.signupMode !== 'city_special') return;
      if (cityFilter && d.cityId !== cityFilter) return;
      const level =
        net.inNetwork
          ? net.level
          : d.signupMode === 'city_special'
            ? 0
            : null;
      if (level == null) return;
      const used = net.inNetwork
        ? net.used
        : Number(d.directSlotsUsed) || 0;
      const total = net.inNetwork
        ? net.total ?? DIRECT_SLOTS_PER_LEVEL ?? 50
        : d.directSlotsTotal != null
          ? Number(d.directSlotsTotal)
          : level >= 3
            ? 0
            : DIRECT_SLOTS_PER_LEVEL || 50;
      rows.push({
        id: d.id,
        firstName: d.firstName,
        lastName: d.lastName,
        email: d.email,
        cityName: d.cityName || '',
        cityId: d.cityId || '',
        networkLevel: level,
        networkLevelLabel: networkLevelLabel
          ? networkLevelLabel(level)
          : `Nivel ${level}`,
        principalUid: net.principalUid || d.principalUid || (level === 0 ? d.id : ''),
        sponsorUid: d.sponsorUid || d.signupViaReferral || '',
        slotsLabel: formatDirectSlots
          ? formatDirectSlots(used, total)
          : `${used}/${total}`,
        slotsUsed: used,
        slotsTotal: total,
      });
    });
    return rows.sort((a, b) => a.networkLevel - b.networkLevel);
  }, [
    drivers,
    cityFilter,
    normalizeNetworkState,
    networkLevelLabel,
    formatDirectSlots,
    DIRECT_SLOTS_PER_LEVEL,
  ]);

  const filteredAlerts = useMemo(() => {
    let list = alerts || [];
    if (cityFilter) list = list.filter((a) => a.cityId === cityFilter);
    if (alertStatusFilter) list = list.filter((a) => a.status === alertStatusFilter);
    return list;
  }, [alerts, cityFilter, alertStatusFilter]);

  const historyRows = useMemo(() => {
    return (history || [])
      .filter((h) => {
        if (!cityFilter) return true;
        const u = usersById[h.leaderUid];
        return u?.cityId === cityFilter;
      })
      .map((h) => {
        const u = usersById[h.leaderUid];
        const dGoal = WEEKLY_MIN_DRIVERS || 2;
        const cGoal = WEEKLY_MIN_CUSTOMERS || 2;
        const ok = h.drivers >= dGoal && h.customers >= cGoal;
        return {
          ...h,
          leaderName: u
            ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
            : h.leaderUid,
          cityName: u?.cityName || '',
          driversLabel: `${h.drivers}/${dGoal}`,
          customersLabel: `${h.customers}/${cGoal}`,
          driversOk: h.drivers >= dGoal,
          customersOk: h.customers >= cGoal,
          metaOk: ok,
        };
      });
  }, [history, cityFilter, usersById, WEEKLY_MIN_DRIVERS, WEEKLY_MIN_CUSTOMERS]);

  const pendingAlerts = alerts.filter((a) => a.status === 'pendiente').length;
  const managedAlerts = alerts.filter(
    (a) => a.status === 'gestionada' || a.status === 'revisada'
  ).length;

  const handleAlertStatus = (row, status) => {
    if (!editLeaderAlert || !settings?.AllowCriticalEditsAdmin) return;
    dispatch(editLeaderAlert({ ...row, status }, 'Update'));
  };

  if (loadingUsers && !staticusers) return <CircularLoading />;
  if (loadingZonif && !cities.length) return <CircularLoading />;

  const masterCode = masterUser?.referralId || '';
  const masterStatus = masterCode
    ? null
    : masterUid
      ? 'La cuenta master no tiene referralId.'
      : 'Configura settings.masterReferrerUid.';

  const copyMasterCode = () => {
    if (!masterCode || !navigator.clipboard) return;
    navigator.clipboard.writeText(masterCode).then(() => setCopied(true)).catch(() => {});
  };

  const leaderGoal = (r) => {
    const net = normalizeNetworkState
      ? normalizeNetworkState(r, r.id)
      : { inNetwork: false };
    const used = net.inNetwork ? net.used : Number(r.directSlotsUsed) || 0;
    const total = net.inNetwork
      ? net.total || 50
      : r.directSlotsTotal != null
        ? Number(r.directSlotsTotal)
        : 50;
    if (used >= total) return 'Completó N1';
    const w = formatWeeklyProgress
      ? formatWeeklyProgress(r.leaderWeekly)
      : { driversOk: false, customersOk: false };
    return w.driversOk && w.customersOk ? 'En meta' : 'Bajo meta';
  };

  const nameCell = (first, last, sub) => (
    <Box sx={{ textAlign: 'left' }}>
      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>
        {`${first || ''} ${last || ''}`.trim() || '—'}
      </Typography>
      {sub ? (
        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>{sub}</Typography>
      ) : null}
    </Box>
  );

  const codeCell = (code) =>
    code ? (
      <Box
        component="span"
        sx={{
          px: 1,
          py: 0.4,
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
          border: `1px dashed ${C2.border}`,
          fontFamily: "'JetBrains Mono', 'Consolas', monospace",
          fontSize: '0.8rem',
          fontWeight: 700,
          color: C2.deep,
          letterSpacing: '0.04em',
        }}
      >
        {code}
      </Box>
    ) : (
      '—'
    );

  const cityColumns = [
    {
      title: 'Ciudad',
      field: 'name',
      render: (r) => nameCell(r.name, '', r.department),
    },
    { title: 'Departamento', field: 'department', hidden: true },
    { title: 'Código maestro ciudad', field: 'masterCode', render: (r) => codeCell(r.masterCode) },
    { title: 'Conductores', field: 'driversCount', type: 'numeric' },
    { title: 'Líderes (1ºs 100)', field: 'leadersCount', type: 'numeric' },
    {
      title: 'Cupos ciudad',
      field: 'slots',
      render: (r) => <C2ProgressBar used={r.slotsUsed} total={r.slotsTotal} done={r.slotsAvailable === 0} />,
    },
    {
      title: 'Disponibles',
      field: 'slotsAvailable',
      type: 'numeric',
      render: (r) => (
        <C2StatusChip
          tone={r.slotsAvailable === 0 ? 'neutral' : 'violet'}
          label={r.slotsAvailable === 0 ? 'Sin cupos' : `${r.slotsAvailable} libres`}
          dot={false}
        />
      ),
    },
  ];

  const leaderColumns = [
    {
      title: 'Líder',
      field: 'firstName',
      render: (r) =>
        nameCell(r.firstName, r.lastName, settings?.AllowCriticalEditsAdmin ? r.email : t('hidden_demo')),
    },
    { title: 'Apellido', field: 'lastName', hidden: true },
    { title: 'Email', field: 'email', hidden: true },
    { title: 'Ciudad', field: 'cityName', render: (r) => r.cityName || '—' },
    {
      title: 'Nivel 1 (cupos)',
      field: 'slots',
      render: (r) => {
        const net = normalizeNetworkState
          ? normalizeNetworkState(r, r.id)
          : { inNetwork: false };
        if (net.inNetwork) return formatDirectSlots(net.used, net.total);
        return formatDirectSlots
          ? formatDirectSlots(
              r.directSlotsUsed || 0,
              r.directSlotsTotal || DIRECT_SLOTS_PER_LEVEL || 50
            )
          : `${r.directSlotsUsed || 0}/50`;
      },
    },
    {
      title: 'Semana cond.',
      field: 'wd',
      render: (r) => {
        const w = formatWeeklyProgress
          ? formatWeeklyProgress(r.leaderWeekly)
          : null;
        return w ? <WeeklyChip ok={w.driversOk} label={w.driversLabel} /> : <WeeklyChip ok={false} label="0/2" />;
      },
    },
    {
      title: 'Semana usu.',
      field: 'wc',
      render: (r) => {
        const w = formatWeeklyProgress
          ? formatWeeklyProgress(r.leaderWeekly)
          : null;
        return w ? <WeeklyChip ok={w.customersOk} label={w.customersLabel} /> : <WeeklyChip ok={false} label="0/2" />;
      },
    },
    {
      title: 'Meta',
      field: 'meta',
      render: (r) => {
        const goal = leaderGoal(r);
        const tone = goal === 'Completó N1' ? 'violet' : goal === 'En meta' ? 'success' : 'warning';
        return <C2StatusChip tone={tone} label={goal} />;
      },
    },
    {
      title: 'Tope mensual',
      field: 'earningsCap',
      render: (r) => {
        const p = api.formatEarningsCapProgress
          ? api.formatEarningsCapProgress(r, r.id)
          : null;
        return p ? p.label : '—';
      },
    },
  ];

  const networkColumns = [
    {
      title: 'Integrante',
      field: 'firstName',
      render: (r) => nameCell(r.firstName, r.lastName, r.cityName),
    },
    { title: 'Apellido', field: 'lastName', hidden: true },
    { title: 'Ciudad', field: 'cityName', hidden: true },
    {
      title: 'Nivel red',
      field: 'networkLevelLabel',
      render: (r) => (
        <C2StatusChip tone={r.networkLevel === 0 ? 'violet' : 'neutral'} label={r.networkLevelLabel} dot={false} />
      ),
    },
    {
      title: 'Estado',
      field: 'networkStructureStatus',
      render: (r) => {
        const u = usersById[r.id] || r;
        return api.networkStructureStatusLabel
          ? api.networkStructureStatusLabel(u.networkStructureStatus)
          : u.networkStructureStatus || '—';
      },
    },
    {
      title: 'Código ingreso',
      field: 'signupEntryCode',
      render: (r) => {
        const u = usersById[r.id] || {};
        return codeCell(u.signupEntryCode || u.citySignupCode || '');
      },
    },
    {
      title: 'Cupos nivel',
      field: 'slotsLabel',
      render: (r) =>
        r.slotsTotal > 0 ? (
          <C2ProgressBar used={r.slotsUsed} total={r.slotsTotal} done={r.slotsUsed >= r.slotsTotal} minWidth={100} />
        ) : (
          <C2StatusChip tone="neutral" label="Sin cupos de invitación" dot={false} />
        ),
    },
    {
      title: 'Tope mensual',
      field: 'earningsCap',
      render: (r) => {
        const u = usersById[r.id] || r;
        const p = api.formatEarningsCapProgress
          ? api.formatEarningsCapProgress(u, r.id)
          : null;
        return p ? p.label : '—';
      },
    },
    {
      title: 'Principal',
      field: 'principalUid',
      render: (r) => {
        const p = usersById[r.principalUid];
        return p
          ? `${p.firstName || ''} ${p.lastName || ''}`.trim() || r.principalUid
          : r.principalUid || '—';
      },
    },
  ];

  const historyColumns = [
    { title: 'Semana', field: 'weekKey' },
    {
      title: 'Líder',
      field: 'leaderName',
      render: (r) => nameCell(r.leaderName, '', r.cityName),
    },
    { title: 'Ciudad', field: 'cityName', hidden: true },
    {
      title: 'Conductores',
      field: 'driversLabel',
      render: (r) => <WeeklyChip ok={r.driversOk} label={r.driversLabel} />,
    },
    {
      title: 'Usuarios',
      field: 'customersLabel',
      render: (r) => <WeeklyChip ok={r.customersOk} label={r.customersLabel} />,
    },
    {
      title: 'Resultado',
      field: 'metaOk',
      render: (r) => (
        <C2StatusChip tone={r.metaOk ? 'success' : 'danger'} label={r.metaOk ? 'Cumplió' : 'No cumplió'} />
      ),
    },
    {
      title: 'Actualizado',
      field: 'updatedAt',
      render: (r) =>
        r.updatedAt ? moment(r.updatedAt).format('lll') : '—',
    },
  ];

  const alertColumns = [
    {
      title: 'Fecha',
      field: 'createdAt',
      render: (r) => (r.createdAt ? moment(r.createdAt).format('lll') : '—'),
    },
    { title: 'Semana', field: 'weekKey' },
    {
      title: 'Conductor',
      field: 'leaderName',
      render: (r) => nameCell(r.leaderName, '', r.cityName),
    },
    { title: 'Ciudad', field: 'cityName', hidden: true },
    {
      title: 'Avance',
      field: 'progress',
      render: (r) =>
        `Cond. ${r.driversRegistered || 0}/${r.driversGoal || 2} · Usu. ${
          r.customersRegistered || 0
        }/${r.customersGoal || 2}`,
    },
    { title: 'Incumplió', field: 'missedLabel' },
    {
      title: 'Estado',
      field: 'status',
      render: (r) => (
        <Select
          size="small"
          value={r.status || 'pendiente'}
          disabled={!settings?.AllowCriticalEditsAdmin}
          onChange={(e) => handleAlertStatus(r, e.target.value)}
          sx={{
            minWidth: 130,
            borderRadius: '10px',
            fontFamily: C2_FONT,
            fontSize: '0.85rem',
            color: C2.dark,
            '& fieldset': { borderColor: C2.border },
            '&.Mui-focused fieldset': { borderColor: C2.violet },
          }}
        >
          <MenuItem value="pendiente" sx={{ fontFamily: C2_FONT }}>Pendiente</MenuItem>
          <MenuItem value="revisada" sx={{ fontFamily: C2_FONT }}>Revisada</MenuItem>
          <MenuItem value="gestionada" sx={{ fontFamily: C2_FONT }}>Gestionada</MenuItem>
        </Select>
      ),
    },
  ];

  const baseOptions = (pageSize) => ({ ...c2TableOptions(null), paging: true, pageSize });
  const tableStyle = c2TableStyle(isRTL);
  const emptyLoc = (icon, title, message) =>
    c2Localization(localization(t), <C2EmptyState icon={icon} title={title} message={message} />);
  const cityFilterLabel = cityFilter ? ' en esta ciudad' : '';

  const alertsToolbar = c2Toolbar(
    <C2Select
      id="red-alert-status-filter"
      label="Estado"
      value={alertStatusFilter}
      onChange={(e) => setAlertStatusFilter(e.target.value)}
      minWidth={170}
      options={[
        { value: '', label: 'Todas' },
        { value: 'pendiente', label: 'Pendiente' },
        { value: 'revisada', label: 'Revisada' },
        { value: 'gestionada', label: 'Gestionada' },
      ]}
    />
  );

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <ThemeProvider theme={c2TableTheme}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} lg={5}>
            <Box
              sx={{
                height: '100%',
                p: { xs: 2, sm: 2.5 },
                borderRadius: '18px',
                background: `linear-gradient(150deg, ${C2.sidebar} 0%, ${C2.deep} 100%)`,
                boxShadow: '0 12px 28px rgba(26,15,59,0.25)',
                color: C2.white,
              }}
            >
              <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Cuenta madre · código abierto
              </Typography>
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: 'rgba(251,250,254,0.75)', mt: 0.5, mb: 1.75, lineHeight: 1.45 }}>
                Código público ilimitado. Al registrar, el sistema clasifica según ciudad: líder si hay cupo de los 100, o referido fuera de red si el cupo ya está lleno.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.25,
                  borderRadius: '14px',
                  backgroundColor: 'rgba(251,250,254,0.08)',
                  border: '1px solid rgba(118,40,255,0.55)',
                  boxShadow: '0 0 18px rgba(100,17,237,0.25)',
                  mb: 1.25,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: 'rgba(251,250,254,0.65)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Código master
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: masterCode ? "'JetBrains Mono', 'Consolas', monospace" : C2_FONT,
                      fontWeight: 800,
                      fontSize: masterCode ? '1.35rem' : '0.9rem',
                      letterSpacing: masterCode ? '0.08em' : 0,
                      color: masterCode ? C2.white : '#FCD34D',
                    }}
                  >
                    {masterCode || masterStatus}
                  </Typography>
                </Box>
                {masterCode ? (
                  <Tooltip title={copied ? '¡Copiado!' : 'Copiar código'}>
                    <IconButton
                      onClick={copyMasterCode}
                      sx={{
                        color: C2.white,
                        background: `linear-gradient(135deg, ${C2.violet}, ${C2.bright})`,
                        '&:hover': { background: 'linear-gradient(135deg, #5B0FD9, #6D1FF0)' },
                      }}
                    >
                      {copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
              {masterUser ? (
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: 'rgba(251,250,254,0.8)', mb: 1 }}>
                  {masterUser.firstName} {masterUser.lastName}
                  {masterUser.email && settings?.AllowCriticalEditsAdmin ? ` · ${masterUser.email}` : ''}
                </Typography>
              ) : null}

              <InfoRow label="Distribución red">
                {NETWORK_DISTRIBUTION_LABEL || '8% / 6% / 2% = 16%'}
              </InfoRow>
              <InfoRow label="Techo mensual líderes">
                {api.formatCop ? api.formatCop(api.EARNINGS_CAP_LEADER || 50000000) : '$50.000.000'}
              </InfoRow>
              <InfoRow label="Techo mensual posteriores">
                {api.formatCop ? api.formatCop(api.EARNINGS_CAP_STANDARD || 30000000) : '$30.000.000'}
              </InfoRow>
              <InfoRow label="Retiros días 15 y 30">
                Líderes 25M + 25M · posteriores 15M + 15M
              </InfoRow>
            </Box>
          </Grid>
          <Grid item xs={12} lg={7}>
            <Grid container spacing={2} sx={{ height: { lg: 'calc(100% + 16px)' } }}>
              <Grid item xs={12} sm={6}>
                <C2SummaryCard icon={LocationCityOutlinedIcon} label="Ciudades activas" value={cityStats.length} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <C2SummaryCard icon={StarsOutlinedIcon} label="Líderes (1ºs 100)" value={leaders.length} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <C2SummaryCard icon={DirectionsCarOutlinedIcon} label="Conductores totales" value={drivers.length} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <C2SummaryCard
                  icon={NotificationsActiveOutlinedIcon}
                  label="Alertas pendientes"
                  value={pendingAlerts}
                  caption={`${managedAlerts} revisadas o gestionadas`}
                  accent={pendingAlerts > 0 ? '#D97706' : '#16A34A'}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 480px', minWidth: 0 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={c2PillTabsSx}
            >
              <Tab icon={<LocationCityOutlinedIcon fontSize="small" />} iconPosition="start" label="Ciudades y cupos" />
              <Tab icon={<StarsOutlinedIcon fontSize="small" />} iconPosition="start" label="Primeros 100 / Cumplimiento" />
              <Tab icon={<AccountTreeOutlinedIcon fontSize="small" />} iconPosition="start" label="Red N1–N3" />
              <Tab icon={<HistoryOutlinedIcon fontSize="small" />} iconPosition="start" label="Historial semanal" />
              <Tab
                icon={
                  <Badge
                    badgeContent={pendingAlerts}
                    max={99}
                    sx={{ '& .MuiBadge-badge': { backgroundColor: '#EF4444', color: '#fff', fontFamily: C2_FONT, fontWeight: 700 } }}
                  >
                    <NotificationsActiveOutlinedIcon fontSize="small" />
                  </Badge>
                }
                iconPosition="start"
                label="Alertas"
              />
              <Tab icon={<SwapHorizRoundedIcon fontSize="small" />} iconPosition="start" label="Cambio de líder" />
            </Tabs>
          </Box>
          <C2Select
            id="red-city-filter"
            label="Filtrar por ciudad"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            minWidth={220}
            options={[
              { value: '', label: 'Todas' },
              ...cityStats.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
        </Box>

        <TabPanel value={tab} index={0}>
          <C2TableCard>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
                mb: 0.5,
              }}
            >
              <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                <C2SectionHeading
                  title="Ciudades activas"
                  subtitle="Cupos de los primeros 100 líderes por ciudad. Usa «Sincronizar cupos» si la barra no coincide con líderes reales."
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<SyncRoundedIcon />}
                onClick={handleSyncCitySlots}
                disabled={syncingSlots || !reconcileCityLeaderSlots}
                sx={{
                  textTransform: 'none',
                  fontFamily: C2_FONT,
                  fontWeight: 700,
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  background: driftedCityCount
                    ? `linear-gradient(135deg, ${C2.violet} 0%, ${C2.bright || '#7628FF'} 100%)`
                    : C2.deep,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none', filter: 'brightness(1.06)' },
                }}
              >
                {syncingSlots
                  ? 'Sincronizando…'
                  : driftedCityCount
                    ? `Sincronizar cupos (${driftedCityCount})`
                    : 'Sincronizar cupos'}
              </Button>
            </Box>
            {syncNotice ? (
              <Alert
                severity={syncNotice.severity}
                onClose={() => setSyncNotice(null)}
                sx={{ mb: 1.5, borderRadius: '12px', fontFamily: C2_FONT }}
              >
                {syncNotice.message}
              </Alert>
            ) : null}
            {driftedCityCount > 0 && !syncNotice ? (
              <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '12px', fontFamily: C2_FONT }}>
                {driftedCityCount === 1
                  ? 'Hay 1 ciudad con cupos desfasados respecto a los líderes reales. Pulsa «Sincronizar cupos».'
                  : `Hay ${driftedCityCount} ciudades con cupos desfasados. Pulsa «Sincronizar cupos».`}
              </Alert>
            ) : null}
            <MaterialTable
              title="Ciudades activas"
              columns={cityColumns}
              data={cityStats}
              style={tableStyle}
              options={baseOptions(10)}
              localization={emptyLoc(LocationCityOutlinedIcon, 'Sin ciudades activas', 'Activa ciudades en Zonificación para verlas aquí.')}
            />
          </C2TableCard>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <C2TableCard>
            <C2SectionHeading
              title="Líderes — cumplimiento semanal"
              subtitle={`Meta: ≥${WEEKLY_MIN_DRIVERS || 2} conductores y ≥${WEEKLY_MIN_CUSTOMERS || 2} usuarios por semana.`}
            />
            <MaterialTable
              title="Líderes — cumplimiento semanal"
              columns={leaderColumns}
              data={filteredLeaders}
              style={tableStyle}
              options={baseOptions(10)}
              localization={emptyLoc(StarsOutlinedIcon, `Sin líderes${cityFilterLabel}`, 'Los conductores que se registren con cupo especial de ciudad aparecerán aquí.')}
            />
          </C2TableCard>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <C2TableCard>
            <C2SectionHeading
              title="Estructura de red"
              subtitle="Nivel 0 = líder; niveles 1–3 bajo patrocinador. Cupos: 50 por nivel hasta N2; N3 sin cupos de invitación."
            />
            <MaterialTable
              title="Estructura de red"
              columns={networkColumns}
              data={networkRows}
              style={tableStyle}
              options={baseOptions(15)}
              localization={emptyLoc(AccountTreeOutlinedIcon, `Sin integrantes en la red${cityFilterLabel}`, 'Cuando los líderes inviten conductores, la estructura aparecerá aquí.')}
            />
          </C2TableCard>
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <C2TableCard>
            <C2SectionHeading
              title="Historial de cumplimiento semanal"
              subtitle="Resultado de cada líder por semana."
            />
            <MaterialTable
              title="Historial de cumplimiento semanal"
              columns={historyColumns}
              data={historyRows}
              style={tableStyle}
              options={baseOptions(15)}
              localization={emptyLoc(HistoryOutlinedIcon, `Sin historial${cityFilterLabel}`, 'El historial se genera al cierre de cada semana.')}
            />
          </C2TableCard>
        </TabPanel>

        <TabPanel value={tab} index={4}>
          <C2TableCard>
            <C2SectionHeading
              title="Alertas administrativas"
              subtitle="Incumplimientos de meta semanal. No se suspende automáticamente; el equipo decide."
            />
            <MaterialTable
              title="Alertas administrativas"
              columns={alertColumns}
              data={filteredAlerts}
              style={tableStyle}
              components={alertsToolbar}
              options={baseOptions(10)}
              localization={emptyLoc(NotificationsActiveOutlinedIcon, 'Sin alertas por ahora', 'Cuando un líder no cumpla su meta semanal, la alerta aparecerá aquí.')}
            />
          </C2TableCard>
        </TabPanel>

        <TabPanel value={tab} index={5}>
          <LeaderChangePanel
            leaders={leaders}
            staticusers={staticusers}
            cityFilter={cityFilter}
            settings={settings}
            tableStyle={tableStyle}
            tableOptions={baseOptions(10)}
            emptyLocalization={emptyLoc}
            onChanged={() => dispatch(fetchUsersOnce({ includeLocations: false, force: true }))}
          />
        </TabPanel>
      </ThemeProvider>
    </Box>
  );
}
